import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import { allMedia, getHotTvShows, getLatestMovies, searchMedia } from "@/utils/mediaData";
import { setPageMeta } from "@/utils/seo";
import type { MediaType } from "@/types/media";

const typeTabs: { key: MediaType | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("keyword") ?? "";
  const [input, setInput] = useState(keyword);
  const [type, setType] = useState<MediaType | "all">("all");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setPageMeta(
      keyword ? `“${keyword}”的搜索结果 - 91n影视大全` : "影视搜索 - 91n影视大全",
      "在91n影视大全中搜索你感兴趣的电影、电视剧、演员或类型，快速找到想看的影视内容。"
    );
  }, [keyword]);

  const results = useMemo(() => searchMedia(keyword, type), [keyword, type]);

  const hotLinks = [...getLatestMovies(4), ...getHotTvShows(4)];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { keyword: kw } : {});
  };

  return (
    <div className="w-full">
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">影视搜索</h1>
          <form onSubmit={handleSubmit} className="max-w-2xl">
            <div className="relative flex items-center">
              <i className="ri-search-line absolute left-4 text-foreground-400 text-lg"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、类型或演员名"
                className="w-full h-12 pl-11 pr-28 rounded-md bg-background-50 border border-background-200 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                className="absolute right-1.5 h-9 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        {keyword ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                “<span className="text-primary-500 font-semibold">{keyword}</span>” 的搜索结果，共找到{" "}
                <span className="text-primary-500 font-semibold">{results.length}</span> 条
              </p>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setType(tab.key)}
                    className={`h-8 px-4 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                      type === tab.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-500"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
                {results.map((item) => (
                  <MediaCard key={item.id} item={item} showUpdated={item.type === "tv"} />
                ))}
              </div>
            ) : (
              <div className="py-16 flex flex-col items-center justify-center text-center">
                <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
                </span>
                <p className="text-foreground-800 font-semibold mb-1">没有找到与“{keyword}”相关的内容</p>
                <p className="text-sm text-foreground-500 mb-6">换个关键词试试，或看看下面的热门推荐</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5 w-full max-w-3xl">
                  {hotLinks.slice(0, 4).map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-4">热门推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
              {hotLinks.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
            <p className="mt-6 text-xs text-foreground-400 text-center">
              数据来源为演示内容，共收录 {allMedia.length} 部影视作品
            </p>
          </div>
        )}
      </div>
    </div>
  );
}